import React, { CSSProperties, useContext, useEffect, useState } from 'react';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useLocation } from 'react-router-dom';
import { ViewManagerContext } from 'contexts/ViewManagerContext';
import { useAppSelector } from 'hooks';
import { useSupportApi, Ticket, PlayerContext } from './useSupportApi';
import { useSupportContext } from './SupportContext';

// Values mirror the support-api's existing VALID_CATEGORIES exactly so the API
// stays unchanged and every submission validates.
const CATEGORIES = [
  { value: 'deposit', label: 'Deposit' },
  { value: 'withdrawal', label: 'Withdrawal' },
  { value: 'account', label: 'Account' },
  { value: 'game_issue', label: 'Game Issue' },
  { value: 'gameplay', label: 'Gameplay' },
  { value: 'tournament', label: 'Tournament' },
  { value: 'bug', label: 'Bug Report' },
  { value: 'other', label: 'Other' },
];

type SupportLandingProps = {
  onOpenTicket: (ticket: Ticket) => void;
  onCreated: (ticket: Ticket) => void;
};

const SupportLanding = ({ onOpenTicket, onCreated }: SupportLandingProps) => {
  const { getTickets, createTicket } = useSupportApi();
  const { unreadByTicket, markTicketRead } = useSupportContext();
  const location = useLocation();
  const { activeView, activeTableInfo } = useContext(ViewManagerContext);
  const game = useAppSelector((state) => state.currentTable.game);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [category, setCategory] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const fetchTickets = async () => {
    try {
      const res = await getTickets();
      const all: Ticket[] = res?.tickets || [];
      // Show tickets from last 7 days, sorted by most recently updated
      const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
      const recent = all
        .filter(t => new Date(t.createdAt).getTime() > cutoff)
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
      setTickets(recent);
    } catch {
      setError('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenTicket = (t: Ticket) => {
    // Opening a ticket marks it read — clear its unread count (persisted in
    // context so it stays cleared when this list remounts). This also
    // decrements the header badge, which is the sum of per-ticket unread.
    markTicketRead(t.ticketId);
    onOpenTicket(t);
  };

  const handleCreate = async () => {
    if (!category || !message.trim()) return;
    setCreating(true);
    setError('');
    try {
      const g = game as any;
      const context: PlayerContext = {
        url: location.pathname + location.search,
        view: activeView,
        timestamp: new Date().toISOString(),
      };
      if (activeTableInfo?.gameID) {
        context.gameID = activeTableInfo.gameID;
        context.gameType = activeTableInfo.gameType;
      }
      if (g?.gameNo) context.gameNo = String(g.gameNo);
      if (g?.gameID) context.gameID = String(g.gameID);
      if (g?.game) context.gameType = g.game;
      if (g?.blindLevels) context.blindLevels = g.blindLevels;
      if (g?.hand) context.hand = String(g.hand);

      const res = await createTicket(category, message.trim(), subject.trim() || undefined, context);
      if (res?.ticket) {
        // Show the "Wire Dispatched" confirmation rather than jumping straight
        // into the chat thread.
        onCreated(res.ticket);
      } else if (res?.error) {
        setError(res.error);
      }
    } catch {
      setError('Failed to create ticket');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return <div style={loadingStyle}>Loading...</div>;
  }

  if (showNew) {
    return (
      <div style={formContainerStyle}>
        <div style={formTitleStyle}>New Support Ticket</div>

        <label style={labelStyle}>Category</label>
        <select
          style={selectStyle}
          value={category}
          onChange={(e) => setCategory(e.target.value)}
        >
          <option value="" style={{ backgroundColor: '#1a2332' }}>Select a category...</option>
          {CATEGORIES.map(c => (
            <option key={c.value} value={c.value} style={{ backgroundColor: '#1a2332' }}>
              {c.label}
            </option>
          ))}
        </select>

        <label style={labelStyle}>Subject (optional)</label>
        <input
          style={inputStyle}
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Brief description"
        />

        <label style={labelStyle}>Message</label>
        <textarea
          style={textareaStyle}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Describe your issue..."
          rows={4}
        />

        {error && <div style={errorStyle}>{error}</div>}

        <div style={btnRowStyle}>
          <button style={secondaryBtnStyle} onClick={() => setShowNew(false)}>
            Cancel
          </button>
          <button
            style={{
              ...primaryBtnStyle,
              opacity: category && message.trim() && !creating ? 1 : 0.5,
            }}
            onClick={handleCreate}
            disabled={!category || !message.trim() || creating}
          >
            {creating ? 'Creating...' : 'Submit'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={landingContainerStyle}>
      <div style={welcomeStyle}>How can we help?</div>

      <button style={newIssueBtnStyle} onClick={() => setShowNew(true)}>
        <AddCircleIcon style={{ fontSize: '1.2rem' }} />
        New Issue
      </button>

      {error && <div style={errorStyle}>{error}</div>}

      {tickets.length > 0 && (
        <>
          <div style={sectionTitleStyle}>Recent Tickets</div>
          <div style={ticketListStyle}>
            {tickets.map((t) => {
              const isOpen = t.status === 'open' || t.status === 'assigned';
              const ticketUnread = unreadByTicket[t.ticketId] || 0;
              const hasNotification = ticketUnread > 0;
              return (
                <button
                  key={t.ticketId}
                  style={{
                    ...ticketCardStyle,
                    ...(hasNotification ? ticketCardNotifiedStyle : {}),
                  }}
                  onClick={() => handleOpenTicket(t)}
                >
                  <div style={ticketIconStyle}>
                    {isOpen
                      ? <ChatBubbleOutlineIcon style={{ fontSize: '1rem', color: '#E98C26' }} />
                      : <CheckCircleOutlineIcon style={{ fontSize: '1rem', color: '#628B48' }} />
                    }
                  </div>
                  <div style={ticketInfoStyle}>
                    <div style={ticketSubjectStyle}>
                      {hasNotification && <span style={unreadDotStyle} />}
                      {t.subject || t.category}
                    </div>
                    <div style={ticketMetaStyle}>
                      {hasNotification ? (
                        <span style={{ color: '#E98C26', fontWeight: 600 }}>New reply</span>
                      ) : (
                        <span style={{
                          color: isOpen ? '#E98C26' : '#628B48',
                          textTransform: 'capitalize',
                        }}>
                          {t.status}
                        </span>
                      )}
                      {' \u00B7 '}
                      {formatRelative(t.updatedAt)}
                    </div>
                  </div>
                  {/* Show a badge only for unread agent replies (count of new
                      messages), not the ticket's total message count. Once the
                      ticket is read the badge disappears entirely. */}
                  {ticketUnread > 0 && (
                    <div style={msgCountNotifiedStyle}>
                      {ticketUnread > 9 ? '9+' : ticketUnread}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default SupportLanding;

function formatRelative(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/* ── Styles ── */

const loadingStyle: CSSProperties = {
  padding: '2.5rem',
  textAlign: 'center',
  color: 'rgba(255,255,255,0.3)',
  fontSize: '0.82rem',
};

const landingContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.7rem',
  padding: '1rem 1.1rem',
};

const welcomeStyle: CSSProperties = {
  fontSize: '1.6rem',
  fontWeight: 700,
  fontFamily: "'Rye', serif",
  color: '#E98C26',
  textAlign: 'center',
  letterSpacing: '0.01em',
  padding: '0.75rem 0.5rem 0.3rem',
};

const newIssueBtnStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.45rem',
  padding: '0.7rem',
  background: 'linear-gradient(180deg, #f5a030 0%, #E98C26 45%, #c97010 100%)',
  // Dark ink text + icon to match the saloon's other orange buttons (e.g. the
  // lobby's active HOLD'EM/OMAHA chips), instead of white. The icon inherits
  // this color, so the filled AddCircleIcon renders as solid ink.
  color: '#1a0e06',
  border: 'none',
  borderRadius: '9px',
  fontSize: '1rem',
  fontWeight: 400,
  // Western serif (same face as the "How can we help?" heading) in title case,
  // matching the Support design — replaces the uppercase Oswald sans label.
  fontFamily: "'Rye', serif",
  letterSpacing: '0.01em',
  cursor: 'pointer',
  minHeight: '46px',
  boxShadow: '0 4px 18px rgba(233,140,38,0.35)',
};

const sectionTitleStyle: CSSProperties = {
  fontSize: '0.62rem',
  fontWeight: 700,
  fontFamily: "'Oswald', sans-serif",
  color: 'rgba(212,168,83,0.5)',
  textTransform: 'uppercase',
  letterSpacing: '0.12em',
  marginTop: '0.25rem',
};

const ticketListStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.3rem',
};

const ticketCardStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.6rem',
  padding: '0.6rem 0.7rem',
  background: 'rgba(255,255,255,0.03)',
  border: '1px solid rgba(212,168,83,0.1)',
  borderRadius: '9px',
  cursor: 'pointer',
  width: '100%',
  textAlign: 'left',
  fontFamily: "'Outfit', sans-serif",
  transition: 'background 0.12s ease, border-color 0.12s ease',
};

const ticketIconStyle: CSSProperties = {
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
};

const ticketInfoStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
};

const ticketSubjectStyle: CSSProperties = {
  fontSize: '0.83rem',
  fontWeight: 600,
  color: 'rgba(255,255,255,0.88)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const ticketMetaStyle: CSSProperties = {
  fontSize: '0.68rem',
  color: 'rgba(255,255,255,0.35)',
  marginTop: '0.1rem',
};

const msgCountStyle: CSSProperties = {
  flexShrink: 0,
  fontSize: '0.65rem',
  color: 'rgba(255,255,255,0.3)',
  background: 'rgba(255,255,255,0.06)',
  borderRadius: '8px',
  padding: '0.18rem 0.45rem',
  fontWeight: 600,
};

const msgCountNotifiedStyle: CSSProperties = {
  ...msgCountStyle,
  color: '#fff',
  background: '#E98C26',
};

const ticketCardNotifiedStyle: CSSProperties = {
  borderColor: 'rgba(233,140,38,0.3)',
  background: 'rgba(233,140,38,0.05)',
};

const unreadDotStyle: CSSProperties = {
  display: 'inline-block',
  width: '5px',
  height: '5px',
  borderRadius: '50%',
  backgroundColor: '#E98C26',
  marginRight: '0.3rem',
  verticalAlign: 'middle',
};

/* ── New ticket form ── */

const formContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.55rem',
  padding: '1rem 1.1rem',
};

const formTitleStyle: CSSProperties = {
  fontSize: '0.85rem',
  fontWeight: 700,
  // Match the saloon "typewriter" label style used in Account Profile so all
  // panels share the same uppercase-tracked heading look.
  fontFamily: "'Special Elite', monospace",
  color: 'rgba(255,255,255,0.92)',
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  marginBottom: '0.15rem',
};

const labelStyle: CSSProperties = {
  fontSize: '0.7rem',
  fontWeight: 700,
  fontFamily: "'Special Elite', monospace",
  color: '#d9c79e',
  textTransform: 'uppercase',
  letterSpacing: '0.18em',
};

// Input borders match Account Profile's orange family (saloonTokens.orange500
// at 40%) so the Support form, Account Profile editor, and other saloon
// panels share the same accent.
const selectStyle: CSSProperties = {
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(232,130,31,0.4)',
  borderRadius: '8px',
  color: 'rgba(255,255,255,0.85)',
  fontSize: '0.84rem',
  fontFamily: "'Inter', system-ui, sans-serif",
  padding: '0.58rem 0.7rem',
  outline: 'none',
};

const inputStyle: CSSProperties = {
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(232,130,31,0.4)',
  borderRadius: '8px',
  color: '#fff',
  fontSize: '0.84rem',
  fontFamily: "'Inter', system-ui, sans-serif",
  padding: '0.58rem 0.7rem',
  outline: 'none',
};

const textareaStyle: CSSProperties = {
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(232,130,31,0.4)',
  borderRadius: '8px',
  color: '#fff',
  fontSize: '0.84rem',
  fontFamily: "'Inter', system-ui, sans-serif",
  padding: '0.58rem 0.7rem',
  outline: 'none',
  resize: 'vertical',
  lineHeight: 1.45,
  minHeight: '90px',
};

const errorStyle: CSSProperties = {
  color: '#e05555',
  fontSize: '0.75rem',
  textAlign: 'center',
  background: 'rgba(220,50,50,0.08)',
  padding: '0.4rem',
  borderRadius: '6px',
};

const btnRowStyle: CSSProperties = {
  display: 'flex',
  gap: '0.45rem',
  marginTop: '0.2rem',
};

const primaryBtnStyle: CSSProperties = {
  flex: 1,
  padding: '0.7rem',
  background: 'linear-gradient(135deg, #E98C26 0%, #c97010 100%)',
  color: '#fff',
  border: 'none',
  borderRadius: '9px',
  fontSize: '0.85rem',
  fontWeight: 700,
  fontFamily: "'Outfit', sans-serif",
  letterSpacing: '0.04em',
  cursor: 'pointer',
  minHeight: '46px',
  transition: 'opacity 0.15s ease',
  boxShadow: '0 4px 14px rgba(233,140,38,0.22)',
};

const secondaryBtnStyle: CSSProperties = {
  flex: 1,
  padding: '0.7rem',
  background: 'rgba(20,35,65,0.95)',
  color: 'rgba(255,255,255,0.75)',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: '9px',
  fontSize: '0.85rem',
  fontWeight: 700,
  fontFamily: "'Outfit', sans-serif",
  cursor: 'pointer',
  minHeight: '46px',
};
